/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Dropdown,
  Select,
  Spin,
  Tooltip,
  message,
} from "antd";
import { EditOutlined, MenuOutlined, UnlockOutlined } from "@ant-design/icons";

import api from "@/utils/api";
import http from "@/utils/http";
import commonFn from "@/utils/commonFn";
import EnhancedTable from "@/hocComponents/EnhancedTable";

import BSModal from "./component/BSModal";
import CTModal from "./component/CTModal";
import BalanceModal from "./component/BalanceModal";
import UploadTransactionsModal from "./component/UploadModal";
import "./component/index.css";
import dayjs from "dayjs";
/* ------------------------------ constants ------------------------------ */

const MAX_TRUSTS = 5;
const SEARCH_DEBOUNCE_MS = 300;
const hasAuth = (key) => commonFn.btnAuthorityFlag(key);

const UPLOAD_ACTIONS = [
  { auth: "UploadBAIFile", type: "bai", label: "Upload BAI File" },
  { auth: "UploadTransactions", type: "transactions", label: "Upload Transactions" },
  { auth: "UploadBalance", type: "balance", label: "Upload Balance" },
];

const INITIAL_FILTERS = {
  name: "",
  bank: "",
  trust: "",
  trustName: "",
  ownerName: "",
  glAcct: "",
  cpAcct: "",
  remark: "",
};

/* ------------------------------- helpers ------------------------------- */

// 差额不为 0 时高亮，兼容 "1,234.50" / "(1,234.50)" / "-1,234.50"
const isNonZeroAmount = (str) => {
  if (!str) return false;
  const n = parseFloat(String(str).replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n !== 0;
};

// bankInfo 下的文本列：统一生成，避免重复
const bankInfoColumn = (title, field, width, placeholder) => ({
  title,
  dataIndex: ["bankInfo", field],
  width,
  filterKey: field,
  filterPlaceholder: placeholder || `Search ${title}`,
  render: (_, record) => record?.bankInfo?.[field] ?? "",
});

const CellLink = ({ disabled, onClick, children }) =>
  disabled ? (
    <span className="br-link br-link--disabled">{children}</span>
  ) : (
    <a className="br-link" onClick={onClick}>
      {children}
    </a>
  );

const ViewingBy = ({ email, loading, onLock }) => {
  if (!email) return null;
  return (
    <span className="br-viewing">
      <Tooltip title={loading ? "Processing..." : "Lock this bank record"}>
        <span
          className={`br-viewing__lock${loading ? " is-loading" : ""}`}
          onClick={loading ? undefined : onLock}
        >
          {loading ? <Spin size="small" /> : <UnlockOutlined />}
        </span>
      </Tooltip>
      <span>{email}</span>
    </span>
  );
};

/* ------------------------------ component ------------------------------ */

export default function DashboardRequest() {
  // table
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  // toolbar
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
const [selectedTrustId, setSelectedTrustId] = useState(undefined);
  const [trustOptions, setTrustOptions] = useState([]);
  const [trustLoading, setTrustLoading] = useState(false);

  // modals
  const [bsModal, setBsModal] = useState({ open: false, record: null });
  const [ctModal, setCtModal] = useState({ open: false, record: null });
  const [balanceModal, setBalanceModal] = useState({ open: false, record: null });
  const [uploadModal, setUploadModal] = useState({ open: false, type: "transactions" });

  const [lockLoadingId, setLockLoadingId] = useState(null);
  const debounceTimer = useRef(null);

  
  /* ------------------------------- trusts ------------------------------- */

  const fetchTrustList = async (content = "") => {
    setTrustLoading(true);
    try {
      const res = await http.get(api.getTrustDictList, { params: { content } });
      const list = res?.data?.trust || res?.data || [];
      setTrustOptions(
        list.map((t) => ({ label: t.trustNameTitle, value: t.trustId })),
      );
    } catch (err) {
      console.error("fetchTrustList error:", err);
    } finally {
      setTrustLoading(false);
    }
  };

 const handleTrustChange = (value) => {
  setSelectedTrustId(value);
  if (value === undefined) fetchTrustList(""); // 清除后恢复完整列表
};

  const handleTrustSearch = (value) => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => fetchTrustList(value),
      SEARCH_DEBOUNCE_MS,
    );
  };

  useEffect(() => {
    fetchTrustList();
    return () => clearTimeout(debounceTimer.current); // 卸载时清理定时器
  }, []);

  /* -------------------------------- table -------------------------------- */

  const fetchTable = async () => {
    setLoading(true);
    try {
      const res = await http.get(api.getBankRecList, {
        params: {
          reconcileDateSt: selectedDate,
          trustIdList: selectedTrustId ?? "",
          ...filters,
        },
      });

      if (res?.code === "0") {
        message.error(res?.msg || "System error.");
        setTableData([]);
        return;
      }

      setTableData(
        (res?.data || []).map((item, idx) => ({
          ...item,
          index: idx + 1,
          key: item.bankInfo?.bankId || idx,
        })),
      );
    } catch (err) {
      console.error("fetchTable error:", err);
      message.error("Request failed, please try again.");
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  /* ------------------------------- actions ------------------------------- */

  const requireDate = () => {
    if (selectedDate) return true;
    message.warning("Please select a date first.");
    return false;
  };

  const onOpenUT = (record) => {
    if (!requireDate()) return;
    const storageKey = `ut_page_${Date.now()}`;
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        record,
        reconcileDateSt: selectedDate,
        token: localStorage.getItem("tokenPC"),
      }),
    );
    window.open(`/#/BankReconciliation/BankRec/UTPage?key=${storageKey}`, "_blank");
  };

  const onOpenBai = (record) => {
    if (!requireDate()) return;
    setBsModal({ open: true, record });
  };

  const onOpenBalance = (record) => setBalanceModal({ open: true, record });

  const onUpload = (type) => setUploadModal({ open: true, type });

  const onViewOutstanding = () =>
    window.open("#/BankReconciliation/BankRec/component/OutstandPage");

  const handleMenuClick = ({ key }, record) => {
    if (key === "cleared") setCtModal({ open: true, record });
  };

  const handleLockBank = async (e, record) => {
    e?.stopPropagation?.();
    const id = record?.bankId;
    if (!id || lockLoadingId === id) return;

    setLockLoadingId(id);
    try {
      const res = await http.get(api.getLockBankRecDelete, {
        params: { bankId: id, reconcileDateSt: selectedDate },
      });
      if (res?.code === "1") {
        message.success("Locked!");
        fetchTable();
      } else {
        message.error(res?.msg || "Lock failed");
      }
    } catch (err) {
      console.error(err);
      message.error("Lock failed");
    } finally {
      setLockLoadingId(null);
    }
  };

  /* ------------------------------- columns ------------------------------- */

  const renderOutstandingLink = (authKey) => (text, record) => (
    <CellLink
      disabled={!!record?.email || !hasAuth(authKey)}
      onClick={() => onOpenUT(record)}
    >
      {text}
    </CellLink>
  );

  const columns = [
    {
      title: "Action",
      dataIndex: "bankId",
      align: "center",
      width: 60,
      render: (_, record) => {
        if (!hasAuth("UnreconciledTrans")) return null;
        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "cleared",
                  icon: <EditOutlined />,
                  label: "Unreconciled Trans",
                },
              ],
              onClick: (e) => handleMenuClick(e, record),
            }}
          >
            <MenuOutlined className="br-action-trigger" />
          </Dropdown>
        );
      },
    },
    bankInfoColumn("Owner", "ownerName", 160),
    bankInfoColumn("Trust", "trust", 80),
    bankInfoColumn("Entity Name", "trustName", 200, "Search Entity"),
    bankInfoColumn("Bank #", "bank", 120),
    bankInfoColumn("Bank", "name", 200),
    bankInfoColumn("Account #", "cpAcct", 160),
    bankInfoColumn("GL Acct", "glAcct", 100),
    bankInfoColumn("Remark", "remark", 200),
    {
      title: "Book Balance",
      dataIndex: "bookBalanceStr",
      className: "br-num",
    },
    {
      title: "Bank Balance",
      dataIndex: "bankBalanceStr",
      className: "br-num",
      render: (text, record) => (
        <CellLink
          // disabled={!record.hasBaiFile || !hasAuth("BankBalance")}
          onClick={() => onOpenBalance(record)}
        >
          {text}
        </CellLink>
      ),
    },
    {
      title: "Difference",
      dataIndex: "differenceStr",
      className: "br-num",
      render: (text) => (
        <span className={isNonZeroAmount(text) ? "br-num--alert" : undefined}>
          {text}
        </span>
      ),
    },
    {
      title: "Book Outstanding CR",
      dataIndex: "bookCrRecord",
      align: "center",
      width: 100,
      render: renderOutstandingLink("BookOutstandingCR"),
    },
    {
      title: "Bank Outstanding Transaction",
      dataIndex: "unreconcilableRecord",
      align: "center",
      width: 100,
      render: renderOutstandingLink("BankOutstandingTransaction"),
    },
    {
      title: "BAI Sweep",
      dataIndex: "baiSweep",
      align: "center",
      width: 100,
      render: (text, record) => (
        <CellLink
          disabled={!hasAuth("BAISweep.")}
          onClick={() => onOpenBai(record)}
        >
          {text}
        </CellLink>
      ),
    },
    {
      title: "Viewing By",
      dataIndex: "email",
      width: 100,
      render: (email, record) => (
        <ViewingBy
          email={email}
          loading={lockLoadingId === record?.bankId}
          onLock={(e) => handleLockBank(e, record)}
        />
      ),
    },
  ];

  /* -------------------------------- render -------------------------------- */

  return (
    <div className="bankrec-page">
      <section className="bankrec-toolbar">
        <div className="bankrec-toolbar__filters">
          {/* <DatePicker
            format="MM/DD/YYYY"
            placeholder="Reconcile date"
            onChange={(date) => setSelectedDate(date ? date.format("YYYY-MM-DD") : null)}
          /> */}
          <DatePicker
         format="MM/DD/YYYY"
          placeholder="Reconcile date"
          value={selectedDate ? dayjs(selectedDate) : null}
           onChange={(date) => setSelectedDate(date ? date.format("YYYY-MM-DD") : null)}
           />
      <Select
  className="bankrec-toolbar__trust"
  showSearch
  allowClear
  placeholder="Select Trust"
  value={selectedTrustId}
  options={trustOptions}
  loading={trustLoading}
  filterOption={false}
  onChange={handleTrustChange}
  onSearch={handleTrustSearch}
  notFoundContent={trustLoading ? "Loading..." : "No data"}
/>
          <Button type="primary" loading={loading} onClick={fetchTable}>
            Submit
          </Button>
        </div>

        <div className="bankrec-toolbar__actions">
          {hasAuth("OutstandingTransaction") && (
            <Button type="primary" onClick={onViewOutstanding}>
              Outstanding Transaction
            </Button>
          )}
          {UPLOAD_ACTIONS.filter((a) => hasAuth(a.auth)).map((a) => (
            <Button key={a.type} type="primary" onClick={() => onUpload(a.type)}>
              {a.label}
            </Button>
          ))}
        </div>
      </section>

      <section className="bankrec-table-card">
        <EnhancedTable
          className="bankrec-table"
          columns={columns}
          dataSource={tableData}
          loading={loading}
          scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
          filters={filters}
          onFilterChange={handleFilterChange}
          onFilterSubmit={fetchTable}
        />
      </section>

      <BSModal
        open={bsModal.open}
        record={bsModal.record}
        reconcileDateSt={selectedDate}
        onClose={() => setBsModal({ open: false, record: null })}
        onSuccess={fetchTable}
      />
      <CTModal
        open={ctModal.open}
        record={ctModal.record}
        reconcileDateSt={selectedDate}
        onClose={() => setCtModal({ open: false, record: null })}
        onSuccess={fetchTable}
      />
      <BalanceModal
        open={balanceModal.open}
        record={balanceModal.record}
        reconcileDateSt={selectedDate}
        onClose={() => setBalanceModal({ open: false, record: null })}
        onSuccess={fetchTable}
      />
      <UploadTransactionsModal
        key={uploadModal.type}
        open={uploadModal.open}
        type={uploadModal.type}
        onClose={() => setUploadModal((s) => ({ ...s, open: false }))}
      />
    </div>
  );
}
